import React, { useEffect } from "react";
import { useTranslate } from "#src/hooks";
import { Form, FormInstance } from "antd";
import styled from "styled-components";

const FONT_INTER = '"Inter Variable", "Inter", sans-serif';

interface VideoSharePasswordConfigProps {
  onConfirm?: (values: FormValues) => void;
  initialPassword?: string;
  form?: FormInstance<FormValues>;
  onValidationChange?: (isValid: boolean) => void;
  size?: "xsmall" | "small" | "medium" | "large";
  border?: boolean;
}

export interface FormValues {
  password: string;
  confirmPassword: string;
}

export const VideoSharePasswordConfigComponent: React.FC<
  VideoSharePasswordConfigProps
> = ({ onConfirm, initialPassword = "", form, onValidationChange }) => {
  const t = useTranslate();

  const password = Form.useWatch("password", form);
  const confirmPassword = Form.useWatch("confirmPassword", form);

  useEffect(() => {
    const isPasswordValid = password && password.length >= 8;
    const doPasswordsMatch = password === confirmPassword;
    const isValid = isPasswordValid && doPasswordsMatch && !!confirmPassword;

    onValidationChange?.(isValid);
  }, [password, confirmPassword, onValidationChange]);

  const handleFinish = (values: FormValues) => {
    onConfirm?.(values);
  };

  return (
    <Wrapper>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          password: initialPassword,
          confirmPassword: initialPassword,
        }}
      >
        <FieldGroup>
          <PasswordLabel htmlFor="password">
            {t("projects.components.VideoSharePasswordConfig.password")}
            <PasswordHint>
              {t("projects.components.VideoSharePasswordConfig.passwordHint")}
            </PasswordHint>
          </PasswordLabel>
          <Form.Item
            name="password"
            noStyle
            rules={[
              {
                min: 8,
                message: t(
                  "projects.components.VideoSharePasswordConfig.passwordMinLength"
                ),
              },
            ]}
          >
            <PasswordInput
              id="password"
              data-testid="password-input"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </Form.Item>
          {password && password.length > 0 && password.length < 8 && (
            <PasswordError>
              {t(
                "projects.components.VideoSharePasswordConfig.passwordMinLength"
              )}
            </PasswordError>
          )}
        </FieldGroup>

        <FieldGroup>
          <PasswordLabel htmlFor="confirmPassword">
            {t("projects.components.VideoSharePasswordConfig.confirmPassword")}
          </PasswordLabel>
          <Form.Item name="confirmPassword" noStyle dependencies={["password"]}>
            <PasswordInput
              id="confirmPassword"
              data-testid="confirm-password-input"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </Form.Item>
          {confirmPassword && password && confirmPassword !== password && (
            <PasswordError>
              {t(
                "projects.components.VideoSharePasswordConfig.passwordsDoNotMatch"
              )}
            </PasswordError>
          )}
        </FieldGroup>
      </Form>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const FieldGroup = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PasswordLabel = styled.label`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-family: ${FONT_INTER};
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
`;

const PasswordHint = styled.span`
  font-family: ${FONT_INTER};
  font-size: 11px;
  font-weight: 400;
  color: #9ca3af;
`;

const PasswordInput = styled.input`
  width: 100%;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #374151;
  padding: 8px 12px;
  height: 36px;
  outline: none;
  background: #f9fafb;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    border-color: #14b8a6;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordError = styled.div`
  font-family: ${FONT_INTER};
  font-size: 11px;
  color: #ef4444;
  margin-top: 4px;
`;
